import { Text, Select } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMaterials } from "#modules/materials/view/useMaterials";
import { PartDto } from "#modules/parts/infra/Part.dto";
import { useUpdatePart } from "#modules/parts/view/useUpdatePart";
import { useUpdatePartType } from "#modules/parts/view/useUpdatePartType";
import {
  buildSelectOptionValue,
  findMaterialFromSelectOptions,
  getMaterialSelectOptions,
} from "#modules/quoteRequestLines/domain/getMaterialSelectOptions";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { useUpdateMaterial } from "#modules/quoteRequestLines/useUpdateMaterial";
import { getSelectablePartTypeOptions, iconForPartType } from "#shared/helpers/partTypeOptions";
import { Icon } from "#shared/icons/Icon";
import { Group } from "#shared/view/components/Group";
import { Title } from "#shared/view/components/Title";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";
import { PartTypeSchema, PartTypeType, QuoteRequestLine } from "shared";

export const Row = ({ label, value }: { label: string; value: React.ReactNode }) => {
  return (
    <Group justify="space-between" py={3} wrap="nowrap" gap="sm">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" fw={500} ta="right">
        {value ?? "–"}
      </Text>
    </Group>
  );
};

const ANALYSIS_PART_TYPE_MAP: Partial<Record<string, PartTypeType>> = {
  sheet: PartTypeSchema.enum.SHEET,
  consumable: PartTypeSchema.enum.CONSUMABLE,
  machining: PartTypeSchema.enum.MACHINING,
  square_tube: PartTypeSchema.enum.SQUARE_TUBE,
  round_tube: PartTypeSchema.enum.ROUND_TUBE,
  assembly: PartTypeSchema.enum.ASSEMBLY,
};

function PartTypeRow({
  part,
  dataPartType,
  quoteRequestLine,
}: {
  part: PartDto;
  dataPartType: string | null | undefined;
  quoteRequestLine: QuoteRequestLine;
}) {
  const { t } = useTranslation();
  const { updatePartType } = useUpdatePartType({
    partId: part.id,
    quoteRequestLine,
  });

  const suggestedType = dataPartType ? (ANALYSIS_PART_TYPE_MAP[dataPartType] ?? null) : null;
  const value = part.type ?? suggestedType;
  const options = getSelectablePartTypeOptions(value);

  return (
    <Group justify="space-between" py={3} wrap="nowrap" gap="sm">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {t("part.analysis2D.partType")}
      </Text>
      <Select
        value={value}
        data={options}
        renderOption={({ option }) => (
          <Group gap="xxs">
            <Icon name={iconForPartType[option.value]} size={12} />
            {option.label}
          </Group>
        )}
        onChange={(val: PartTypeType | null) => {
          if (val) {
            updatePartType({ newPartType: val, newPartSubType: null });
          }
        }}
        size="xs"
        w="60%"
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
      />
    </Group>
  );
}

function MaterialRow({ quoteRequestLineId }: { quoteRequestLineId: string }) {
  const { t } = useTranslation();
  const { data: quoteRequestLine } = useQuery(quoteRequestLineQuery({ id: quoteRequestLineId }));
  const { materials } = useMaterials();
  const { updateMaterial } = useUpdateMaterial();

  const currentGrade = quoteRequestLine?.materialGrade ?? null;
  const currentName = quoteRequestLine?.materialName ?? "";
  const currentValue = currentGrade ? buildSelectOptionValue(currentGrade, currentName) : null;
  const options = useMemo(
    () => getMaterialSelectOptions(materials, currentName, currentGrade),
    [materials, currentName, currentGrade],
  );

  const handleChange = (val: string | null) => {
    if (!val) {
      return;
    }
    const { materialName, materialGrade } = findMaterialFromSelectOptions(val, materials);
    void updateMaterial({
      quoteRequestLineId,
      predictionStatus: quoteRequestLine?.predictionStatus ?? null,
      payload: { materialName, materialGrade },
    });
  };

  return (
    <Group justify="space-between" py={3} wrap="nowrap" gap="sm">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {t("part.analysis2D.materialGrade")}
      </Text>
      <Select
        value={currentValue}
        data={options}
        onChange={handleChange}
        size="xs"
        w="60%"
        searchable
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
      />
    </Group>
  );
}

function NameRow({ part }: { part: PartDto }) {
  const { t } = useTranslation();
  const [name, setName] = useState(part.name);
  const { updatePart } = useUpdatePart(part.id);

  const handleBlur = () => {
    if (!name.trim() || name === part.name) {
      return;
    }
    updatePart({ id: part.id, payload: { name } });
  };

  return (
    <Group justify="space-between" py={3} wrap="nowrap" gap="sm">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {t("part.analysis2D.name")}
      </Text>
      <TextInput
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={handleBlur}
        size="xs"
        w="60%"
      />
    </Group>
  );
}

const SectionHeader = ({ part }: { part: PartDto }) => {
  const { t } = useTranslation();

  return (
    <>
      <Title order={5} my={6}>
        {t("part.analysis2D.sections.information")}
      </Title>
      <NameRow part={part} />
    </>
  );
};

export const AnalysisResultInformationSection = ({
  part,
  quoteRequestLine,
}: {
  part: PartDto;
  quoteRequestLine: QuoteRequestLine | null;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <SectionHeader part={part} />
      {quoteRequestLine ? (
        <PartTypeRow part={part} dataPartType={part.type} quoteRequestLine={quoteRequestLine} />
      ) : (
        <Row label={t("part.analysis2D.partType")} value={part.type} />
      )}
      {quoteRequestLine ? (
        <MaterialRow quoteRequestLineId={quoteRequestLine.id} />
      ) : (
        <>
          <Row label={t("part.analysis2D.materialGrade")} value={part.materialGrade} />
          <Row label={t("part.analysis2D.materialDescription")} value={part.materialName} />
        </>
      )}
    </>
  );
};
